import React, { useState } from 'react';
import toast from 'react-hot-toast';
import SceneTabsBar from './SceneTabsBar';
import CourseChapterPanel from './CourseChapterPanel';
import PracticePanel from './PracticePanel';
import LearningContentViewer from './LearningContentViewer';
import LearningZoneQuiz from './LearningZoneQuiz';
import ActionSongsView from './ActionSongsView';
import StoriesView from './StoriesView';
import ArtTherapyView from './ArtTherapyView';

const PRACTICE_NOUNS = { scene: 'scene', story: 'story', poem: 'poem', revision: 'revision' };

// Screens with a look of their own (`content.screen.kind`, see addContentScreens); every other row
// keeps the Scenes look below. Each gets the same behaviour from here (Art Therapy has no Your
// Practice: its drawing page saves the artwork for coach review instead).
const SCREENS = { songs: ActionSongsView, stories: StoriesView, art: ArtTherapyView };
const SCREEN_PRACTICE = {
  songs: 'Record yourself doing the actions and upload it.',
  stories: 'Record yourself for this story and upload it.',
};

// A row shows the lessons of the rows under it too (Action Songs: Vocal and Instrumental Songs), and
// each row numbers its own ("Action Song 1" in both): a title shown more than once says which row
// it is from (`rowLabel`, courseSubcategoryRows.js), "Action Song 1 · Vocal Songs".
const lessonsOf = (chapters) => {
  const times = new Map();
  chapters.forEach((chapter) => times.set(chapter.title, (times.get(chapter.title) || 0) + 1));
  return chapters.map((chapter) =>
    times.get(chapter.title) > 1 && chapter.rowLabel ? { ...chapter, title: `${chapter.title} · ${chapter.rowLabel}` } : chapter
  );
};

/**
 * A course task type row (e.g. English > Revision), laid out like the Scenes
 * screen: a tab per chapter of the row (whatever course it is saved in: students
 * don't see courses), the selected chapter's content items, and "Your
 * Practice". `content` is { title, category, taskType, chapters, screen? } from
 * addCourseTaskTypeRows (or the subcategory rows, courseSubcategoryRows.js). A row of
 * Action Songs, Stories or Art Therapy (`screen`) shows its own screen instead, with the same
 * behaviour.
 */
export default function CourseContentView({ content }) {
  const { title, taskType, chapters, screen } = content;
  const Screen = SCREENS[screen?.kind];
  const [activeId, setActiveId] = useState(chapters[0]?.id);
  const [openItem, setOpenItem] = useState(null);
  // The quiz item being taken; it shows in place of the chapter's cards.
  const [quizItem, setQuizItem] = useState(null);

  if (!chapters.length) {
    // Action Songs / Stories / Art Therapy keep their own heading and say so under it.
    if (Screen) return <Screen title={title} screen={screen} lessons={[]} />;
    return (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 text-center">
        <h2 className="text-[26px] font-extrabold text-slate-900">{title}</h2>
        <p className="mt-2 text-[15px] font-semibold text-gray-500">No lessons have been added here yet.</p>
      </div>
    );
  }

  const lessons = lessonsOf(chapters);
  const chapter = lessons.find((entry) => entry.id === activeId) || lessons[0];
  const trackerTopic = chapter.trackerTopic || chapter.syllabusTopic;
  const quiz = quizItem && quizItem.chapterId === chapter.id ? quizItem : null;
  const pickChapter = (id) => {
    setActiveId(id);
    setQuizItem(null);
  };

  // `lesson`: the lesson the item belongs to, when it isn't the selected one (the Art Therapy list
  // shows every lesson's buttons at once); it becomes the selected lesson for its quiz.
  const handleOpenItem = (item, lesson = chapter) => {
    if (item.type === 'quiz') {
      if (!item.quizId) {
        toast.error('This quiz is not available');
        return;
      }
      // Taken here, in place of the cards; it submits like the student quiz page (marks the item
      // done and awards coins). Named like its card: "Action Song 1 Quiz".
      const quizCount = lesson.items.filter((entry) => entry.type === 'quiz').length;
      setActiveId(lesson.id);
      setQuizItem({ ...item, chapterId: lesson.id, name: `${lesson.title} ${quizCount > 1 ? item.title : 'Quiz'}` });
      return;
    }
    if (item.type === 'link') {
      if (!item.externalUrl) {
        toast.error('This game link is not available');
        return;
      }
      window.open(item.externalUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    setOpenItem(item);
  };

  const quizPanel = quiz && (
    <LearningZoneQuiz
      key={quiz.id}
      title={quiz.name}
      quizId={quiz.quizId}
      courseId={chapter.courseId}
      contentItemId={quiz.id}
      onClose={() => setQuizItem(null)}
    />
  );
  // An item numbered in a coach Syllabus Tracker topic ("Action Song 3"), or in a subcategory the
  // tracker shows on its own (trackerTopic), sends the practice to that session; others stay
  // local, as before.
  const practice = (
    <PracticePanel
      sceneId={chapter.id}
      description={SCREEN_PRACTICE[screen?.kind] || `Record yourself for this ${PRACTICE_NOUNS[taskType] || 'lesson'} and upload it.`}
      {...(trackerTopic && chapter.sessionNumber ? { topic: trackerTopic, sessionNumber: chapter.sessionNumber } : {})}
    />
  );

  return (
    <>
      {Screen ? (
        <Screen
          title={title}
          screen={screen}
          lessons={lessons}
          activeId={chapter.id}
          onSelect={pickChapter}
          onOpenItem={handleOpenItem}
          quiz={quizPanel}
          practice={practice}
        />
      ) : (
        <>
          <SceneTabsBar scenes={lessons} activeSceneId={chapter.id} onSelectScene={pickChapter} />

          {/* Same row as the Scenes screen: the card and Your Practice side by side from 900px. */}
          <div className="grid grid-cols-1 [@container_(min-width:900px)]:grid-cols-[minmax(0,1fr)_minmax(360px,33.333%)] gap-6 items-start">
            <div className="min-w-0">{quizPanel || <CourseChapterPanel chapter={chapter} onOpenItem={handleOpenItem} />}</div>
            {practice}
          </div>
        </>
      )}

      {openItem && <LearningContentViewer item={openItem} onClose={() => setOpenItem(null)} />}
    </>
  );
}
